// Workflows (docs/12 "Workflows, versions and the marketplace", D53): the templates admins build
// and publish, filtered by status (chips), task type and a search, all in the URL. Creating one opens a
// drawer and then its editor; deleting is refused while task groups use it (the API says so).
import { FlowArrow, Plus } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Button } from "rizzui";

import { createWorkflow, deleteWorkflow, setWorkflowStatus, taskTypesQuery, workflowsQuery } from "../catalog/api";
import { SearchBox } from "../catalog/SearchBox";
import { useUrlSearch } from "../catalog/useUrlSearch";
import { TEMPLATE_STATUSES, type TemplateStatus, type WorkflowInput, type WorkflowSummary } from "../catalog/types";
import { WorkflowForm } from "../catalog/WorkflowForm";
import { useT } from "../i18n";
import { sectionCrumbs, SECTIONS } from "../layout/nav";
import { toast } from "../layout/toast";
import { cardClass } from "../ui/Card";
import { cn } from "../ui/cn";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { FilterChips } from "../ui/FilterChips";
import { PageHeader } from "../ui/PageHeader";
import { SelectField, SideDrawer } from "../ui/SideDrawer";
import { EmptyState, ErrorState, Loading } from "../ui/states";
import { statusLabel } from "../ui/tones";
import { WorkflowsTable } from "../workflows/list/WorkflowsTable";

type StatusFilter = "all" | TemplateStatus;
const capitalised = (s: string) => s.charAt(0).toLocaleUpperCase() + s.slice(1);
const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

function counts(rows: WorkflowSummary[]): Record<StatusFilter, number> {
  const c = { all: rows.length, draft: 0, active: 0, disabled: 0, archived: 0 };
  for (const w of rows) c[w.status]++;
  return c;
}

export default function WorkflowsPage() {
  const { common, workflows: { list: t } } = useT();
  const crumbs = sectionCrumbs("workflows");
  const chips: { key: StatusFilter; label: string }[] = [
    { key: "all", label: common.states.all },
    ...TEMPLATE_STATUSES.map((s) => ({ key: s, label: capitalised(statusLabel(s)) })),
  ];
  const [params, setParams] = useSearchParams();
  const status: StatusFilter = TEMPLATE_STATUSES.find((s) => s === params.get("status")) ?? "all";
  const taskType = params.get("type") ?? "";
  const search = useUrlSearch();
  const workflows = useQuery({ ...workflowsQuery({ q: search.q }), placeholderData: (previous) => previous });
  const taskTypes = useQuery(taskTypesQuery());
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<WorkflowSummary>();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["catalog", "workflows"] });

  const setFilter = (patch: { status?: StatusFilter; type?: string }) => {
    const next = { status, type: taskType, q: search.q, ...patch };
    setParams(
      Object.fromEntries(Object.entries(next).filter(([, v]) => v !== "" && v !== "all")),
      { replace: true },
    );
  };
  const ofType = (workflows.data ?? []).filter((w) => taskType === "" || w.task_type_id === taskType);
  const rows = ofType.filter((w) => status === "all" || w.status === status);

  async function create(input: WorkflowInput) {
    const created = await createWorkflow(input);
    await refresh();
    toast(t.created(created.name));
    void navigate(`/workflows/${created.id}`);
  }

  async function changeStatus(w: WorkflowSummary, next: TemplateStatus) {
    try {
      await setWorkflowStatus(w.id, next);
      await refresh();
      toast(t.nowStatus(w.name, statusLabel(next)));
    } catch (e) {
      toast(message(e), "warn");
    }
  }

  async function remove(w: WorkflowSummary) {
    await deleteWorkflow(w.id);
    await refresh();
    toast(t.deleted(w.name));
  }

  return (
    <>
      <PageHeader title={SECTIONS.workflows.title} breadcrumb={crumbs} description={SECTIONS.workflows.subtitle}>
        <Button className="gap-1.5" onClick={() => setCreating(true)} disabled={!taskTypes.data?.length}>
          <Plus weight="bold" className="h-4 w-4" /> {t.newWorkflow}
        </Button>
      </PageHeader>
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <FilterChips label={t.filterByStatus} chips={chips} value={status} counts={counts(ofType)} onChange={(s) => setFilter({ status: s })} />
        <SearchBox className="md:ms-auto md:w-64" label={t.search} placeholder={t.search} text={search.text} onChange={search.setText} />
        <SelectField
          className="md:w-56"
          label={t.taskType}
          value={taskType}
          options={[{ value: "", label: t.allTaskTypes }, ...(taskTypes.data ?? []).map((tt) => ({ value: tt.id, label: tt.name }))]}
          onChange={(type) => setFilter({ type })}
        />
      </div>
      <div className={cn(cardClass, "p-3 lg:p-4")}>
        {workflows.isPending ? <Loading /> : null}
        {workflows.isError ? <ErrorState title={t.loadError} message={workflows.error.message} /> : null}
        {workflows.isSuccess && rows.length === 0 ? (
          <EmptyState compact icon={<FlowArrow weight="duotone" />} title={workflows.data.length === 0 && !search.q ? t.emptyTitle : t.noMatchTitle}>
            {workflows.data.length === 0 && !search.q
              ? t.emptyBody
              : t.noMatchBody}
          </EmptyState>
        ) : null}
        {rows.length > 0 ? (
          <WorkflowsTable rows={rows} taskTypes={taskTypes.data ?? []} actions={{ setStatus: (w, s) => void changeStatus(w, s), remove: setDeleting }} />
        ) : null}
      </div>
      <SideDrawer open={creating} onClose={() => setCreating(false)} title={t.newWorkflow} subtitle={t.drawerSubtitle}>
        {creating && taskTypes.data ? (
          <WorkflowForm taskTypes={taskTypes.data} submitLabel={t.create} onSubmit={create} onCancel={() => setCreating(false)} />
        ) : null}
      </SideDrawer>
      <ConfirmDialog
        open={deleting !== undefined}
        label={t.deleteTitle(deleting?.name ?? "")}
        title={t.deleteTitle(deleting?.name ?? "")}
        confirmLabel={common.actions.delete}
        onClose={() => setDeleting(undefined)}
        onConfirm={async () => deleting && remove(deleting)}
      >
        {t.deleteBody}
      </ConfirmDialog>
    </>
  );
}
